import React, { useContext, useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { CustomerContext } from '../../../contexts/CustomerContext'
import useSchools from '../../../hooks/useSchools'
import useQueryString from '../../../hooks/use-query-string'
import Cart from '../../../models/Cart'
import { OrderPackage, Student, School } from '../../../models'
import Button from '../../Button'
import Typography from '../../Typography'
import { routes } from '../../../routes'
import { customStyles } from '../schedule-appointment'
import classes from '../schedule-appointment/schedule-appointment.module.sass'
import { resolveUpgradeEmail } from './resolve-upgrade-email'

// Customer self-serve reschedule entry. Identifies the customer's current booked
// senior appointment either by (a) the booking flow's student-ID lookup (school +
// email + student ID, no login) OR (b) a signed token from the confirmation-email
// link (?token=...), then offers two paths:
//   * Change date/time -> the same picker in "reschedule" mode (a free move).
//   * Change session/upgrade -> the EXISTING senior upgrade->checkout flow,
//     which collects the price difference (is_upgrade OrderPackage).
const RescheduleAppointment = () => {
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const schools = useSchools()
  // `upgrade` = an Option id from a staff-sent SMS link: skip the choice screen and
  // go straight into the upgrade flow with that option pre-selected.
  const { token, upgrade } = useQueryString()
  const autoUpgradeStarted = useRef(false)

  const [school, setSchool] = useState('')
  const [email, setEmail] = useState(customer.email || '')
  const [studentId, setStudentId] = useState('')
  const [status, setStatus] = useState(token ? 'loading' : 'idle') // idle | loading | not_found | no_appt | error | token_invalid
  const [view, setView] = useState('form') // form | choice
  const [student, setStudent] = useState(null)
  const [appointment, setAppointment] = useState(null) // the current booked OrderPackage
  const [resolvedSchool, setResolvedSchool] = useState(null) // the School model to use downstream

  const OP_INCLUDES = ['apiOption', 'package', 'cart', 'student']

  // (b) Tokenized email link: resolve the token to an OrderPackage id, load the
  // appointment + its student/school, and jump straight to the choice screen.
  useEffect(() => {
    if (!token) return
    let active = true

    // baseURL '/' bypasses the global axios /api/v1 prefix: reschedule_lookup is a
    // root route (public + throttled), not an /api/v1 resource.
    axios.get('/reschedule_lookup', { params: { token }, baseURL: '/' })
      // filter[student_id] is required of non-admins by Api::OrderPackagesController#index.
      .then(({ data }) => OrderPackage
        .where({ id: data.order_package_id, student_id: data.student_id })
        .includes(OP_INCLUDES).first())
      .then(({ data: op }) => {
        if (!op) throw new Error('appointment not found')
        return School.find(op.schoolId).then(({ data: sch }) => ({ op, sch }))
      })
      .then(({ op, sch }) => {
        if (!active) return
        setStudent(op.student)
        setAppointment(op)
        setResolvedSchool(sch)
        setEmail(op.cart?.email || '')
        setStatus('idle')
        setView('choice')
      })
      .catch((err) => {
        console.error('reschedule token lookup failed', err)
        if (active) setStatus('token_invalid')
      })

    return () => { active = false }
  }, [token])

  // The student's most recent purchased appointment (the one to move/upgrade).
  const findCurrentAppointment = (foundStudent) =>
    OrderPackage
      .where({ studentId: foundStudent.id, purchased: true })
      .includes(OP_INCLUDES)
      .order({ createdAt: 'desc' })
      .all()
      .then(({ data }) => data.find((op) => !!op.appointmentTime) || null)

  // (a) Form lookup by school + email + student ID.
  const handleLookup = (e) => {
    e.preventDefault()
    if (!school?.value) return toast.error('Please select your school.')
    if (!email) return toast.error('Please enter your email address.')
    if (!studentId) return toast.error('Please enter a Student I.D. #')

    setStatus('loading')
    Student
      .where({ schoolId: school.id, studentId })
      .first()
      .then(({ data: foundStudent }) => {
        if (!foundStudent) { setStatus('not_found'); return null }
        return findCurrentAppointment(foundStudent).then((orderPackage) => {
          if (!orderPackage) { setStatus('no_appt'); return }
          setStudent(foundStudent)
          setAppointment(orderPackage)
          setResolvedSchool(schools.find((s) => s.id === school.value))
          setStatus('idle')
          setView('choice')
        })
      })
      .catch((err) => {
        console.error('reschedule lookup failed', err)
        setStatus('error')
      })
  }

  // Move the date/time of the existing appointment (free). currentOrderPackage is
  // the existing record so the picker PATCHes it; the picker reads `.option`,
  // which the persisted record exposes as `apiOption`.
  const goReschedule = () => {
    appointment.option = appointment.apiOption
    setCustomer({
      ...customer,
      email,
      currentSchool: resolvedSchool,
      currentStudent: student,
      currentOrderPackage: appointment,
      apptCart: appointment.cart,
    })
    history.push(routes.appointmentSelection({ senior: 'true', reschedule: 'true' }))
  }

  // Change the session (a paid upgrade). Hand off to the EXISTING upgrade flow,
  // which detects the prior purchased OrderPackage and charges the difference. It
  // mutates currentOrderPackage into the upgrade purchase, so give it a FRESH
  // skeleton + cart -- never the existing paid record.
  const goUpgrade = (preselectOptionId = null) => {
    // Source the email from the loaded appointment, not the `email` state: on the
    // staff auto-upgrade link, React 17 fires this before setEmail lands, so the
    // state can be blank -- and a blank-email cart is invisible to the checkout's
    // for_email lookup (the customer could never pay). See resolve-upgrade-email.js.
    const upgradeEmail = resolveUpgradeEmail(email, appointment)
    const skeleton = new OrderPackage({
      package: appointment.package,
      packageId: appointment.packageId,
      student,
      studentId: student.id,
    })
    const cart = new Cart({
      createdLocation: 'sittings', email: upgradeEmail, school: resolvedSchool, schoolId: resolvedSchool?.id,
    })
    cart.save()
      .then(() => {
        skeleton.cartId = cart.id
        setCustomer({
          ...customer,
          email: upgradeEmail,
          currentSchool: resolvedSchool,
          currentStudent: student,
          currentOrderPackage: skeleton,
          apptCart: cart,
        })
        history.push(routes.appointmentOptionSelection(appointment.packageId, {
          senior: 'true',
          ...(preselectOptionId ? { upgrade: preselectOptionId } : {}),
        }))
      })
      .catch((err) => {
        console.error('upgrade start failed', err)
        toast.error('We could not start your upgrade. Please try again.')
      })
  }

  // Staff-sent upgrade link (?token=…&upgrade=<optionId>): once the appointment is
  // loaded, jump straight into the upgrade flow with that option pre-selected.
  useEffect(() => {
    if (autoUpgradeStarted.current) return
    if (!upgrade || !appointment || !student || !resolvedSchool) return
    autoUpgradeStarted.current = true
    goUpgrade(upgrade)
  }, [upgrade, appointment, student, resolvedSchool])

  if (token && status === 'loading') {
    return (
      <div className={`${classes.pageContainer} ${classes.senior}`}>
        <div className={classes.container}>
          <Typography classes={classes.instructions} align="center">Finding your appointment…</Typography>
        </div>
      </div>
    )
  }

  // Auto-upgrade in progress (staff link): don't flash the choice screen.
  if (upgrade && status !== 'token_invalid' && (view === 'choice' || status === 'loading')) {
    return (
      <div className={`${classes.pageContainer} ${classes.senior}`}>
        <div className={classes.container}>
          <Typography classes={classes.instructions} align="center">Preparing your upgrade…</Typography>
        </div>
      </div>
    )
  }

  if (view === 'choice') {
    return (
      <div className={`${classes.pageContainer} ${classes.senior}`}>
        <ToastContainer position="bottom-right" />
        <div className={classes.container}>
          <p className={classes.seniorFormTitle}>What would you like to do?</p>
          <Typography classes={classes.instructions} align="center">
            {student?.firstName} {student?.lastName} — {appointment?.apiOption?.name}
          </Typography>
          <div className={classes.form}>
            <Button fullWidth size="large" variant="primary" onClick={goReschedule}>
              Change my date &amp; time
            </Button>
            <div style={{ height: 12 }} />
            {/* onClick passes the click event; call goUpgrade with no preselect so the
                customer sees the full upgrade grid (not ?upgrade=[object Event]). */}
            <Button fullWidth size="large" variant="secondary" onClick={() => goUpgrade()}>
              Change my session (pay any difference)
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${classes.pageContainer} ${classes.senior}`}>
      <ToastContainer position="bottom-right" />
      <div className={classes.container}>
        <p className={classes.seniorFormTitle}>Reschedule Your <em>Appointment</em></p>
        <Typography classes={classes.instructions} align="center">
          Enter your information to find and change your booked appointment.
        </Typography>

        <form onSubmit={handleLookup} className={classes.form}>
          {status === 'token_invalid' && (
            <div className={classes.formAlert} role="alert">
              That reschedule link is invalid or has expired. Please look up your appointment below.
            </div>
          )}
          {status === 'not_found' && (
            <div className={classes.formAlert} role="alert">
              We couldn&apos;t find a student with that I.D. at this school. Please double-check and try again.
            </div>
          )}
          {status === 'no_appt' && (
            <div className={classes.formAlert} role="alert">
              We couldn&apos;t find a booked appointment for that student. If you haven&apos;t booked yet,
              please use “Book Appointment” instead.
            </div>
          )}
          {status === 'error' && (
            <div className={classes.formAlert} role="alert">
              Something went wrong. Please try again.
            </div>
          )}

          <label className={`${classes.label} ${classes.select}`}>
            <span>Your School:</span>
            <Select
              styles={customStyles}
              defaultValue={school}
              options={schools.map((s) => ({ value: s.id, label: s.name, schoolTypeId: s.schoolTypeId, id: s.id }))}
              onChange={setSchool}
            />
          </label>
          <label className={classes.label}>
            <span>Your Email Address:</span>
            <input type="email" placeholder="Email" value={email} onChange={(evt) => setEmail(evt.target.value)} />
          </label>
          <label className={classes.label}>
            <span>Student I.D. #:</span>
            <input type="text" placeholder="Student I.D." value={studentId}
                   onChange={(e) => { setStudentId(e.target.value); if (status !== 'idle') setStatus('idle') }} />
          </label>
          <Button fullWidth size="large" type="submit" variant="primary" disabled={status === 'loading'}>
            {status === 'loading' ? 'Finding your appointment…' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default RescheduleAppointment
